import {
  Button,
  ButtonGroup,
  cn,
  DateInput,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Select,
  SelectItem,
} from '@heroui/react'
import { getLocalTimeZone, parseDate, today } from '@internationalized/date'
import { createFormHook } from '@tanstack/react-form'
import { fieldContext, formContext } from 'contexts/formContext'
import { Check, ChevronDown, ListFilterPlus, Minus, Plus, X } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { convertFiltersToParams } from 'utils/funcs/call-logs/filterFns'
import { booleanOperators, operators } from './constants'

/**
 * Generic, reusable filter bar for call logs and SMS
 *
 * Props:
 *  - filterableFields: Record<string, FieldDefinition>
 *  - appliedFilters: Array<Filter>
 *  - appliedMode: 'AND' | 'OR'
 *  - defaultNewFilter: { field, operator, value }
 *  - extraFormComponents?: { SaveFilterModal?, LoadFilterModal? }
 *  - pathwayFieldKey?: string (e.g. 'pathway_id' vs 'curr_pathway_id')
 */
export default function FilterBarCore({
  filterableFields,
  appliedFilters,
  appliedMode,
  defaultNewFilter,
  extraFormComponents = {},
  pathwayFieldKey,
  className,
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  // wire up TanStack Form
  const { useAppForm } = createFormHook({
    fieldContext,
    formContext,
    formComponents: extraFormComponents,
  })

  const handleApplyFilters = (value, meta = {}) => {
    const mode = meta.mode || appliedMode
    const newSearchParams = convertFiltersToParams(value.filters, mode)
    setSearchParams(newSearchParams, { replace: true })
  }

  const handleClearFilters = () => {
    const keysToDelete = Array.from(searchParams.keys()).filter(key => key.startsWith('filter:'))

    keysToDelete.forEach((key) => {
      searchParams.delete(key)
    })

    setSearchParams(searchParams, { replace: true })
  }

  const form = useAppForm({
    defaultValues: {
      filters: appliedFilters,
    },
    onSubmit({ value, meta }) {
      handleApplyFilters(value, meta)
    },
  })

  const handleModeSelection = (keys) => {
    const selectedKey = Array.from(keys)[0]
    form.handleSubmit({ mode: selectedKey })
  }

  // Helper to get the pathway filter key, defaulting to supplied prop or auto-detecting
  const getPathwayFilterValue = (filters) => {
    const key = pathwayFieldKey
      ?? Object.entries(filterableFields).find(([, def]) => def.inputComponent?.name === 'PathwaySelect')?.[0]
    return filters.find(f => f.field === key)?.value
  }

  return (
    <form.AppForm>
      <form.Field name="filters" mode="array">
        {(filters) => {
          const hasFilters = filters.state.value?.length > 0
          const hasAppliedFilters = appliedFilters?.length > 0
          return (
            <div className={cn('flex w-full flex-col gap-2 shadow-inner', { 'border-b border-gray-200 p-2': hasFilters })}>
              {filters.state.value.map((_, i) => {
                return (
                  <div key={i} className="flex flex-row gap-2">
                    {/* FIELD SELECT */}
                    <form.Field
                      name={`filters[${i}].field`}
                      listeners={{
                        onChange: ({ value }) => {
                          const newField = filterableFields[value]
                          if (!newField)
                            return

                          if (newField.inputComponent === DateInput) {
                            form.setFieldValue(`filters[${i}].value`, today(getLocalTimeZone()))
                          }
                          else if (newField.inputComponent?.name === 'KVInput') {
                            form.setFieldValue(`filters[${i}].value`, {})
                          }
                          else if (newField.inputComponent) {
                            form.setFieldValue(`filters[${i}].value`, '')
                          }
                          else {
                            form.setFieldValue(`filters[${i}].value`, '')
                          }
                          form.setFieldValue(`filters[${i}].operator`, newField.operators[0])
                        },
                      }}
                    >
                      {subField => (
                        <Select
                          selectedKeys={[subField.state.value]}
                          onChange={e => subField.handleChange(e.target.value)}
                          variant="bordered"
                          size="sm"
                          className="max-w-xl"
                          aria-label="Field"
                          classNames={{ popoverContent: 'rounded-sm' }}
                        >
                          {Object.entries(filterableFields).map(([key, field]) => (
                            <SelectItem key={key}>{field.label}</SelectItem>
                          ))}
                        </Select>
                      )}
                    </form.Field>

                    {/* OPERATOR SELECT */}
                    <form.Subscribe
                      selector={state => state.values.filters[i]?.field}
                    >
                      {field => (
                        <form.Field
                          name={`filters[${i}].operator`}
                          listeners={{
                            onChange: ({ value }) => {
                              if (value === 'isNotEmpty') {
                                form.setFieldValue(`filters[${i}].value`, '')
                              }
                            },
                          }}
                        >
                          {(subField) => {
                            const operatorKeys = filterableFields[field]?.operators ?? []
                            return (
                              <Select
                                aria-label="Operator"
                                variant="bordered"
                                size="sm"
                                selectedKeys={[subField.state.value]}
                                onChange={e => subField.handleChange(e.target.value)}
                                classNames={{ base: 'w-fit min-w-32 shrink-0', popoverContent: 'w-64! rounded-sm', innerWrapper: 'w-full' }}
                              >
                                {operatorKeys.map(key => (
                                  <SelectItem key={key}>{operators[key]}</SelectItem>
                                ))}
                              </Select>
                            )
                          }}
                        </form.Field>
                      )}
                    </form.Subscribe>

                    {/* VALUE INPUT */}
                    <form.Subscribe
                      selector={state => ({
                        field: state.values.filters[i]?.field,
                        operator: state.values.filters[i]?.operator,
                        pathwayId: getPathwayFilterValue(state.values.filters),
                      })}
                    >
                      {({ field, operator, pathwayId }) => (
                        <>
                          {!booleanOperators.includes(operator) && (
                            <form.Field name={`filters[${i}].value`}>
                              {(subField) => {
                                const fieldDef = filterableFields[field] ?? {}
                                const Component = fieldDef.inputComponent ?? Input
                                const inputProps = fieldDef.inputProps ?? {}

                                const handleChange = (e) => {
                                  // If event has target treat as input event else raw value
                                  if (e && e.target && Object.prototype.hasOwnProperty.call(e.target, 'value')) {
                                    subField.handleChange(e.target.value)
                                  }
                                  else {
                                    subField.handleChange(e)
                                  }
                                }

                                return (
                                  <Component
                                    aria-label="Value"
                                    onChange={handleChange}
                                    value={Component === DateInput && typeof subField.state.value === 'string' ? parseDate(subField.state.value) : subField.state.value}
                                    variant="bordered"
                                    radius="sm"
                                    size="sm"
                                    pathwayId={pathwayId}
                                    className="flex-1"
                                    classNames={{ popoverContent: 'rounded-sm' }}
                                    {...inputProps}
                                  />
                                )
                              }}
                            </form.Field>
                          )}
                        </>
                      )}
                    </form.Subscribe>

                    {/* REMOVE FILTER BUTTON */}
                    <Button
                      isDisabled={filters.state.value.length === 1 && hasAppliedFilters}
                      size="sm"
                      variant="bordered"
                      isIconOnly
                      onPress={() => filters.removeValue(i)}
                    >
                      <Minus size={12} className="text-gray-500" />
                    </Button>
                  </div>
                )
              })}

              {/* FOOTER AREA */}
              <div className={cn('w-full', { 'flex flex-row justify-between': hasFilters })}>
                {hasFilters && extraFormComponents?.LoadFilterModal && (
                  <div className="flex flex-row gap-2">
                    <form.LoadFilterModal />
                    {hasAppliedFilters && <form.SaveFilterModal />}
                  </div>
                )}

                <div className="flex flex-row gap-2">
                  {hasAppliedFilters && (
                    <Button variant="bordered" size="sm" onPress={handleClearFilters}>
                      <X size={16} />
                      Clear
                    </Button>
                  )}

                  {hasFilters && (
                    <ButtonGroup size="sm" radius="sm" variant="bordered">
                      <Button onPress={form.handleSubmit}>
                        <Check size={16} />
                        Apply all
                      </Button>
                      <Dropdown variant="bordered" size="sm" radius="sm" placement="bottom-end">
                        <DropdownTrigger>
                          <Button isIconOnly>
                            <ChevronDown size={16} />
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                          disallowEmptySelection
                          aria-label="Apply options"
                          className="max-w-[300px]"
                          selectedKeys={[appliedMode]}
                          selectionMode="single"
                          onSelectionChange={handleModeSelection}
                        >
                          <DropdownItem key="AND">Apply with AND</DropdownItem>
                          <DropdownItem key="OR">Apply with OR</DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </ButtonGroup>
                  )}

                  {/* ADD FILTER BTN */}
                  <Button
                    variant={hasFilters ? 'bordered' : 'flat'}
                    size="sm"
                    onPress={() => filters.pushValue(defaultNewFilter)}
                    disableRipple
                    radius={hasFilters ? 'sm' : 'none'}
                    fullWidth={!hasFilters}
                    className={cn('rounded-t-lg bg-white', className)}
                  >
                    {hasFilters
                      ? (
                          <>
                            <Plus size={16} />
                            Add filter
                          </>
                        )
                      : (
                          <>
                            <ListFilterPlus size={16} />
                            Filters
                          </>
                        )}
                  </Button>
                </div>
              </div>
            </div>
          )
        }}
      </form.Field>
    </form.AppForm>
  )
}
